import type { StatusEvidence } from '@/hooks/useStatusEvidence';
import type { CSSProperties, ReactElement } from 'react';
import { sideIcon, sideIconCell } from '@/components/dashboard/sideIconCell';
import { StatusBadge } from '@/components/StatusBadge';
import { REGION_FLAGS, type Region } from '@/constants/regions';
import { formatDate } from '@/lib/format';

export interface SubmissionGroupRowProps {
  readonly statusEvidence?: StatusEvidence;
  readonly nda: string;
  readonly drugName: string;
  /**
   * Applicant name shown as a subtitle under the drug name when
   * `compact` is false. Matches V1 HomeScreen drugCell, which shows
   * the applicant as secondary info under the drug.
   */
  readonly applicant?: string;
  readonly compact?: boolean;
  readonly region: Region | string;
  readonly latestStatus: string | null | undefined;
  readonly submissionCount: number;
  /**
   * phase-fix-dashboard-seq-cell — the Seq cell label. HomePage computes the
   * highest sequence number that is not yet Complete (the active, in-progress
   * sequence); falls back to the highest sequence number when all are Complete,
   * or '—' when there are none. Replaces the old "{n} submissions" count, which
   * didn't tell the operator where the submission is in the process. See
   * `groupSeqLabel` in HomePage.tsx.
   */
  readonly seqLabel: string;
  readonly latestUpdate: string;
  readonly isExpanded: boolean;
  readonly onToggle: () => void;
  /**
   * Phase 5.X-submission-reorder: when present, render the ↕ reorder
   * icon at the right edge of the row. Clicking it opens
   * ReorderModal. The icon is intentionally only rendered when there
   * are at least 2 unlocked sequences (no point reordering one
   * row); HomePage decides that and passes the handler accordingly.
   */
  readonly onReorderClick?: () => void;
  /**
   * Phase 5.X-dashboard-totaldocs — total document count summed across all
   * sequences in the group. `null` / `undefined` renders an em-dash.
   */
  readonly totalDocs?: number | null;
  /**
   * phase-dashboard-coherence item 5 (D-051) — ONE row-click grammar for the
   * whole table: the expand/collapse ARROW toggles only; ROW-click navigates
   * to the parent's face (the highest visible sequence — item 6/D-050, one
   * shared source computed by HomePage via computeParentFace). Absent (no
   * visible face, e.g. all children hidden) → row-click expands only; a row
   * never navigates nowhere.
   */
  readonly onRowNavigate?: () => void;
  /** item 6 (D-050) — truthful sequence-count annotation, e.g. "3 sequences (1 hidden)". */
  /**
   * phase-dashboard-honest-counts (Sharon's D-050 amendment) — the SEQ-cell
   * second line renders ONLY when the group has hidden sequences. Null/absent
   * means nothing hidden → no annotation at all.
   */
  readonly hiddenNote?: string | null;
  /** item 6 (D-050) — explicit all-children-hidden parent state. */
  readonly allHidden?: boolean;
  /**
   * Phase dashboard-ux-fixes TODO-034 — Next Step button on collapsed group row.
   * Label for the most-active sequence in this group (computed by HomePage):
   *   1. Draft sequence exists → "Upload Documents"
   *   2. No Draft → most recently created Complete sequence → "View Submission"
   * If neither condition applies, pass undefined and no button is rendered.
   */
  readonly nextStepLabel?: string;
  /**
   * Phase dashboard-ux-fixes TODO-034 — click handler for the group-level
   * Next Step button. Navigates to the most-active sequence's detail page.
   */
  readonly onNextStepClick?: () => void;
}

const cellStyle: CSSProperties = { padding: '12px 8px', textAlign: 'center' };

export function SubmissionGroupRow({
  statusEvidence,
  nda,
  drugName,
  applicant,
  compact = false,
  region,
  latestStatus,
  submissionCount,
  seqLabel,
  latestUpdate,
  isExpanded,
  onToggle,
  onReorderClick,
  totalDocs,
  onRowNavigate,
  hiddenNote,
  allHidden = false,
  nextStepLabel,
  onNextStepClick,
}: SubmissionGroupRowProps): ReactElement {
  const flag = (REGION_FLAGS as Record<string, string>)[region] ?? '';
  const expandable = submissionCount > 1;
  // item 5 (D-051) — ONE grammar: the ARROW toggles; the ROW navigates to the
  // face. With no face (all hidden / nothing visible) the row falls back to
  // expand-only — it never navigates nowhere.
  const handleRowClick =
    onRowNavigate ?? (expandable ? onToggle : undefined);
  const rowClickable = handleRowClick !== undefined;

  return (
    <tr
      style={{ cursor: rowClickable ? 'pointer' : 'default' }}
      onClick={handleRowClick}
      data-testid={`group-row-${nda}`}
      data-all-hidden={allHidden ? 'true' : 'false'}
      // item 5 — the same hover affordance child rows have, so a navigating
      // parent row reads as clickable.
      onMouseEnter={(e) => {
        if (rowClickable) e.currentTarget.style.background = 'var(--surface2)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = '';
      }}
    >
      <td style={{ ...cellStyle, width: 24, padding: '4px 0' }}>
        {expandable && (
          /* item 5 (D-051) — the chevron is a real BUTTON with a generous
             padded click target: a missed tiny glyph must not become an
             accidental navigation. It alone expands/collapses. */
          <button
            type="button"
            data-testid={`group-expand-${nda}`}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? 'Collapse sequences' : 'Expand sequences'}
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: 10,
              color: 'var(--text-muted)',
              padding: '10px 12px',
              lineHeight: 1,
            }}
          >
            {isExpanded ? '▼' : '▶'}
          </button>
        )}
      </td>
      {/* phase-ui-dashboard-stable-columns-artifact-cleanup — under the
          dashboard's fixed table layout, long names truncate inside the Drug
          column (full text on hover) rather than resizing it. */}
      <td data-card-title="true"
        style={{ ...cellStyle, textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        title={applicant ? `${drugName} — ${applicant}` : drugName}
      >
        <strong>{drugName}</strong>
        {!compact && applicant && (
          <div
            style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {applicant}
          </div>
        )}
      </td>
      <td data-label="Application"
        className="col-shaded"
        style={{ ...cellStyle, fontFamily: 'monospace', color: 'var(--text-muted)', fontSize: 13 }}
      >
        {nda}
      </td>
      <td data-label="Region" style={{ ...cellStyle, fontSize: 14, whiteSpace: 'nowrap' }}>
        <span title={String(region)}>{flag}</span> <span style={{ fontSize: 12 }}>{region}</span>
      </td>
      <td data-label="Latest status" className="col-shaded" style={cellStyle}>
        <StatusBadge status={latestStatus} evidence={statusEvidence} />
      </td>
      {/* Seq cell — phase-dashboard-row-parity: the ↕ reorder control lives
          here, matching V1's `seqCell` (HomeScreen.jsx), styled as V1's
          borderless opacity-hover icon. phase-fix-dashboard-seq-cell: the
          content is the active sequence number (`seqLabel`).
          phase-dashboard-polish-batch (Sharon 2026-07-19): the ↕ sits in the
          cell's LEFT gutter via the shared side-icon structure
          (sideIconCell.ts), so the number stays centered whether or not the
          reorder handle renders. */}
      <td data-label="Sequence"
        data-testid={`group-seq-${nda}`}
        style={sideIconCell({ ...cellStyle, color: 'var(--text-muted)', fontSize: 13, whiteSpace: 'nowrap' })}
      >
        {onReorderClick !== undefined && (
          <button
            type="button"
            onClick={(e) => {
              // Stop the click from triggering the row's `onToggle`.
              e.stopPropagation();
              onReorderClick();
            }}
            aria-label={`Reorder sequences for ${nda}`}
            title="Reorder sequences"
            data-testid={`reorder-icon-${nda}`}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: 12,
              color: 'var(--text-muted)',
              opacity: 0.4,
              padding: 0,
              lineHeight: 1.4,
              transition: 'opacity 0.15s',
              ...sideIcon('left', 2),
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '0.4';
            }}
          >
            ↕
          </button>
        )}
        {allHidden ? (
          <span data-testid={`group-all-hidden-${nda}`} style={{ fontStyle: 'italic' }}>
            all sequences hidden
          </span>
        ) : (
          seqLabel
        )}
        {hiddenNote != null && (
          <div
            data-testid={`group-hidden-note-${nda}`}
            style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}
          >
            {hiddenNote}
          </div>
        )}
      </td>
      {/* phase-dashboard-honest-counts — the parent DOCS number is the sum of
          the SHOWN child rows' per-sequence counts (computeParentFace
          .visibleDocs), so expanding the group always reconstructs it. */}
      <td data-label="Documents"
        className="col-shaded"
        data-testid="docs-cell"
        title="Current documents across the shown sequences (sum of the expanded rows)"
        style={{ ...cellStyle, color: 'var(--text-muted)', fontSize: 12 }}
      >
        {totalDocs != null ? totalDocs : '—'}
      </td>
      {/* Next Step cell — phase-dashboard-row-parity: only the Next Step
          action button lives here, matching V1 (the actionBtn cell). The ↕
          reorder control was moved out of this cell into the Seq cell above. */}
      <td data-label="Next step" style={cellStyle}>
        {/* Phase dashboard-ux-fixes TODO-034 — Next Step button on group row */}
        {nextStepLabel !== undefined && onNextStepClick !== undefined && (
          <button
            type="button"
            // phase-dashboard-polish-batch — the shared fixed-width capsule
            // class (see index.css), so parent and child capsules read as one
            // uniform rail. Sizing only; color/variant untouched.
            className="next-step-btn"
            data-testid={`group-next-step-${nda}`}
            onClick={(e) => {
              e.stopPropagation();
              onNextStepClick();
            }}
            style={{
              fontSize: 11,
              padding: '6px 14px',
              background: 'var(--primary)',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            {nextStepLabel}
          </button>
        )}
      </td>
      <td data-label="Updated"
        className="col-shaded"
        style={{ ...cellStyle, color: 'var(--text-muted)', fontSize: 13, whiteSpace: 'nowrap' }}
      >
        {formatDate(latestUpdate)}
      </td>
    </tr>
  );
}
